import { ChevronDownIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { cn } from '@/lib/cn';

import { SortPopoverContent } from './sort-popover-content';
import type { SortPopoverOptions } from './types';

type Props = {
  titles?: {
    sort?: string;
    order?: string;
  };
  options: SortPopoverOptions[];
  sortValue: string;
  orderValue: string;
  onChangeSort: (props: { sortValue: string; orderValue?: string }) => void;
  trigger?: React.ReactNode;
  triggerClassName?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
};

export const SortPopover = ({
  titles,
  options,
  sortValue,
  orderValue,
  onChangeSort,
  trigger,
  triggerClassName,
  align = 'start',
  side = 'bottom',
}: Props) => {
  const [open, setOpen] = useState(false);

  const currentOption = useMemo(() => {
    return options.find((opt) => opt.sortOption.value === sortValue);
  }, [options, sortValue]);

  const defaultTrigger = (
    <button
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 flex w-fit items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-9",
        triggerClassName,
      )}
    >
      Sort by{' '}
      {typeof currentOption?.sortOption.label === 'string'
        ? currentOption.sortOption.label.toLowerCase()
        : sortValue}
      <ChevronDownIcon className="size-4 opacity-50" />
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger ?? defaultTrigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        className="w-auto p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SortPopoverContent
          titles={titles}
          options={options}
          sortValue={sortValue}
          orderValue={orderValue}
          onChangeSort={onChangeSort}
        />
      </PopoverContent>
    </Popover>
  );
};
